/**
 * Clickjacking defense-in-depth.
 *
 * The authoritative protection lives at the edge:
 *   - `X-Frame-Options: DENY` (Cloudflare Transform Rule)
 *   - `Content-Security-Policy: frame-ancestors 'self' https://*.lovable.app https://*.lovable.dev`
 *     (Cloudflare Transform Rule - browsers IGNORE `frame-ancestors` in <meta>)
 *
 * This module is a runtime fallback that detects when the page is framed by
 * an unexpected origin and breaks out. It also exposes a hook for tests.
 */

const ALLOWED_FRAME_ANCESTORS = [
  /^https:\/\/([a-z0-9-]+\.)*lovable\.app$/i,
  /^https:\/\/([a-z0-9-]+\.)*lovable\.dev$/i,
  /^https:\/\/elementpad\.net$/i,
  /^https:\/\/www\.elementpad\.net$/i,
];

export function isAllowedFrameAncestor(origin: string): boolean {
  return ALLOWED_FRAME_ANCESTORS.some((re) => re.test(origin));
}

export function installFrameGuard(): void {
  if (typeof window === "undefined") return;
  if (window.top === window.self) return; // not framed

  let parentOrigin = "";
  try {
    parentOrigin = window.top?.location.origin ?? "";
  } catch {
    // Cross-origin frame - origin is unreadable. Allow only if document.referrer matches.
    try {
      parentOrigin = new URL(document.referrer).origin;
    } catch {
      parentOrigin = "";
    }
  }

  if (parentOrigin && isAllowedFrameAncestor(parentOrigin)) return;

  // Hostile frame: break out.
  try {
    window.top!.location.href = window.location.href;
  } catch {
    // If we can't redirect the top frame, blank our own document so no
    // clickjacking surface remains.
    document.documentElement.innerHTML = "";
  }
}